<template>
    <div class="ms-page">
        <!-- header -->
        <div class="ms-head">
            <div class="ms-title-wrap">
                <span class="ms-title-ic"><v-icon small>mdi-account-check-outline</v-icon></span>
                <div>
                    <h1 class="ms-title">{{ $t('MySubmissions') }}</h1>
                    <span class="ms-count">{{ mySubmissions.length }}</span>
                </div>
            </div>

            <!-- status filters: none selected = everything -->
            <div class="ms-filters" role="group">
                <button v-for="s in statusOptions"
                        :key="s.key"
                        type="button"
                        class="ms-filter"
                        :class="[`ms-filter--${s.key}`, { 'is-on': activeStatus === s.key }]"
                        :aria-pressed="activeStatus === s.key ? 'true' : 'false'"
                        @click="toggleStatus(s.key)">
                    <span class="ms-filter-dot" aria-hidden="true"></span>
                    <span>{{ $t(s.label) }}</span>
                    <span class="ms-filter-n">{{ countFor(s.key) }}</span>
                </button>
            </div>
        </div>

        <!-- table card -->
        <v-card flat class="ms-card" :loading="loading">
            <div class="ms-toolbar">
                <div class="ms-search">
                    <v-icon small class="ms-search-ic">mdi-magnify</v-icon>
                    <input v-model="search" type="search" class="ms-search-input" :placeholder="$t('Search')" :aria-label="$t('Search')" />
                </div>
            </div>

            <v-data-table :headers="headers"
                          :items="filtered"
                          :search="search"
                          class="ms-table"
                          :footer-props="{ itemsPerPageOptions: [10, 25, 50], showFirstLastPage: false }">
                <template v-slot:[`item.surveyId`]="{ item }">
                    <span class="ms-id">{{ item.surveyId }}</span>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                    <router-link class="ms-name" :to="{ name: 'my-survey-results', params: { id: item.surveyId } }">{{ item.name }}</router-link>
                </template>
                <template v-slot:[`item.participationDate`]="{ item }">
                    <span class="ms-date">
                        <v-icon x-small class="ms-date-ic">mdi-calendar-clock</v-icon>
                        {{ item.participationDate | formatDateTimeGregorian }}
                    </span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <span class="ms-status" :class="`ms-status--${statusKey(item.status)}`">
                        <span class="ms-status-dot" aria-hidden="true"></span>{{ $t(item.status) }}
                    </span>
                </template>
                <template v-slot:[`item.options`]="{ item }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <router-link class="ms-action" :to="{ name: 'my-survey-results', params: { id: item.surveyId } }" v-bind="attrs" v-on="on" :aria-label="$t('Results')">
                                <v-icon small>mdi-chart-box-outline</v-icon>
                            </router-link>
                        </template>
                        <span>{{ $t('Results') }}</span>
                    </v-tooltip>
                </template>
                <template v-slot:no-data>
                    <div class="ms-empty">
                        <v-icon size="36" color="#b8c4c1">mdi-inbox-outline</v-icon>
                        <span>{{ $t('NoItemsToShow') }}</span>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="ms-empty">
                        <v-icon size="36" color="#b8c4c1">mdi-magnify-close</v-icon>
                        <span>{{ $t('NoItemsToShow') }}</span>
                    </div>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
    import SurveyService from '@/services/SurveyService.js'

    export default {

        data() {
            return {
                search: '',
                loading: false,
                activeStatus: null,
                mySubmissions: [],
                statusOptions: [
                    { key: 'approved', label: 'Approved' },
                    { key: 'pending', label: 'Pending' },
                    { key: 'draft', label: 'Draft' }
                ],
                headers: [
                    { text: this.$t('Id'), value: 'surveyId', width: 90 },
                    { text: this.$t('Name'), value: 'name' },
                    { text: this.$t('Date'), value: 'participationDate', width: 210 },
                    { text: this.$t('Status'), value: 'status', width: 150 },
                    { text: this.$t('Options'), value: 'options', align: 'center', sortable: false, width: 90 },
                ],
            }
        },

        computed: {
            filtered() {
                if (!this.activeStatus) return this.mySubmissions
                return this.mySubmissions.filter(x => this.statusKey(x.status) === this.activeStatus)
            }
        },

        created() {
            this.listSruveys()
        },

        methods: {
            statusKey(status) {
                return String(status || '').toLowerCase()
            },
            countFor(key) {
                return this.mySubmissions.filter(x => this.statusKey(x.status) === key).length
            },
            toggleStatus(key) {
                this.activeStatus = this.activeStatus === key ? null : key
            },
            listSruveys() {
                this.loading = true
                SurveyService.listMySubmissionsSurveys().then((res) => {
                    this.mySubmissions = res.data
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
        },
    }
</script>

<style scoped>
    .ms-page {
        --ms-surface: #f3f6f5;
        --ms-line: #e6ecea;
        --ms-text: #1f2a28;
        --ms-muted: #6b7a77;
        --ms-primary: #0a716e;
        --ms-primary-tint: #e6f2f1;
        --ms-amber: #b45309;
        --ms-amber-tint: #fdf3e7;
        --ms-ease: cubic-bezier(.2, .8, .2, 1);
        padding: 22px 20px 32px;
        background: #fff;
    }

    /* ---------- header ---------- */
    .ms-head {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 14px;
        margin-bottom: 16px;
    }

    .ms-title-wrap { display: flex; align-items: center; gap: 12px; }
    .ms-title-wrap > div { display: flex; align-items: center; gap: 10px; }

    .ms-title-ic {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 13px;
        background: var(--ms-primary-tint);
    }
    .ms-title-ic .v-icon { color: var(--ms-primary) !important; }

    .ms-title {
        margin: 0;
        font-size: 22px;
        font-weight: 800;
        line-height: 1.2;
        color: var(--ms-text);
    }

    .ms-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 30px;
        height: 26px;
        padding: 0 9px;
        border-radius: 999px;
        background: var(--ms-primary);
        color: #fff;
        font-size: 12.5px;
        font-weight: 700;
    }

    .ms-filters { display: flex; flex-wrap: wrap; gap: 8px; }

    .ms-filter {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 36px;
        padding: 0 12px 0 10px;
        border: 1px solid var(--ms-line);
        border-radius: 999px;
        background: #fff;
        color: var(--ms-muted);
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color .2s var(--ms-ease), border-color .2s var(--ms-ease), color .2s var(--ms-ease);
    }
    .ms-filter:hover { background: var(--ms-surface); }
    .ms-filter:focus { outline: none; }
    .ms-filter:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ms-primary); }

    .ms-filter-dot { width: 8px; height: 8px; border-radius: 50%; background: #c9d3d0; }
    .ms-filter--approved .ms-filter-dot { background: var(--ms-primary); }
    .ms-filter--pending .ms-filter-dot { background: #f59e0b; }
    .ms-filter--draft .ms-filter-dot { background: #9aa8a5; }

    .ms-filter-n {
        min-width: 20px;
        padding: 0 6px;
        border-radius: 999px;
        background: var(--ms-surface);
        color: var(--ms-text);
        font-size: 11.5px;
        text-align: center;
    }

    .ms-filter.is-on { border-color: var(--ms-primary); color: var(--ms-primary); background: var(--ms-primary-tint); }
    .ms-filter.is-on .ms-filter-n { background: #fff; color: var(--ms-primary); }

    /* ---------- card + toolbar ---------- */
    .ms-card {
        border: 1px solid var(--ms-line) !important;
        border-radius: 18px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important;
        overflow: hidden;
    }

    .ms-toolbar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border-bottom: 1px solid var(--ms-line);
    }

    .ms-search {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        max-width: 380px;
        height: 40px;
        padding: 0 12px;
        border: 1px solid var(--ms-line);
        border-radius: 12px;
        background: var(--ms-surface);
        transition: border-color .2s var(--ms-ease), background-color .2s var(--ms-ease), box-shadow .2s var(--ms-ease);
    }
    .ms-search:focus-within { background: #fff; border-color: var(--ms-primary); box-shadow: 0 0 0 3px var(--ms-primary-tint); }
    .ms-search-ic { color: var(--ms-muted) !important; }
    .ms-search-input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        font: inherit;
        font-size: 13.5px;
        color: var(--ms-text);
    }
    .ms-search-input::placeholder { color: var(--ms-muted); }
    .ms-search-input::-webkit-search-cancel-button { -webkit-appearance: none; }

    /* ---------- table ---------- */
    .ms-table ::v-deep table { border-collapse: separate; }
    .ms-table ::v-deep thead th {
        height: 44px !important;
        font-size: 12px !important;
        font-weight: 700 !important;
        color: var(--ms-muted) !important;
        background: #fafcfb;
        border-bottom: 1px solid var(--ms-line) !important;
        white-space: nowrap;
    }
    .ms-table ::v-deep tbody td {
        height: 56px !important;
        font-size: 13.5px;
        color: var(--ms-text);
        border-bottom: 1px solid #eef2f1 !important;
    }
    .ms-table ::v-deep tbody tr { transition: background-color .15s var(--ms-ease); }
    .ms-table ::v-deep tbody tr:hover { background: var(--ms-surface) !important; }
    .ms-table ::v-deep tbody tr:last-child td { border-bottom: 0 !important; }
    .ms-table ::v-deep .v-data-footer {
        border-top: 1px solid var(--ms-line);
        font-size: 12.5px;
        color: var(--ms-muted);
        padding: 4px 8px;
    }

    .ms-id {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 8px;
        background: var(--ms-surface);
        color: var(--ms-muted);
        font-size: 12px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
    }

    .ms-name {
        font-weight: 700;
        color: var(--ms-text);
        text-decoration: none;
        transition: color .15s var(--ms-ease);
    }
    .ms-name:hover { color: var(--ms-primary); }

    .ms-date {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--ms-muted);
        font-size: 12.5px;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
    .ms-date-ic { color: #9aa8a5 !important; }

    .ms-status {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 26px;
        padding: 0 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        background: var(--ms-surface);
        color: var(--ms-muted);
    }
    .ms-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
    .ms-status--approved { background: var(--ms-primary-tint); color: var(--ms-primary); }
    .ms-status--pending { background: var(--ms-amber-tint); color: var(--ms-amber); }
    .ms-status--draft { background: #eef1f0; color: #5c6b69; }

    .ms-action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border: 1px solid var(--ms-line);
        border-radius: 10px;
        background: #fff;
        text-decoration: none;
        transition: background-color .2s var(--ms-ease), border-color .2s var(--ms-ease);
    }
    .ms-action .v-icon { color: var(--ms-muted) !important; transition: color .2s var(--ms-ease); }
    .ms-action:hover { background: var(--ms-primary-tint); border-color: transparent; }
    .ms-action:hover .v-icon { color: var(--ms-primary) !important; }
    .ms-action:focus-visible { outline: none; box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ms-primary); }

    .ms-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 36px 12px;
        color: var(--ms-muted);
        font-size: 13.5px;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        .ms-page { padding: 14px 12px 24px; }
        .ms-title { font-size: 19px; }
        .ms-search { max-width: none; }
    }

    @media (prefers-reduced-motion: reduce) {
        .ms-filter, .ms-search, .ms-name, .ms-action, .ms-table ::v-deep tbody tr { transition: none; }
    }
</style>
